import * as React from "react";
import * as z from "zod";
import { loginWithEmailAndPassword } from "../api/loginAuth";
import {
  TextField,
  Link,
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  useTheme,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import logoCrypto from "@/assets/logoCrypto.svg";
import inActiveEye from "@/assets/inActiveEye.svg";
import blueEye from "@/assets/blueEye.svg";
import inActiveMail from "@/assets/InActiveMail.png";
import inActiveBlueEye from "@/assets/inActiveBlueEye.svg";
import activeMail from "@/assets/activeMail.svg";
import waving from "@/assets/waving.svg";
import { makeStyles } from "@material-ui/core/styles";
import { Navigate, useSearchParams } from "react-router-dom";
import storage from "@/utils/storage";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, Formik } from "formik";


const formSchema = z.object({
  email: z
    .string()
    .superRefine((val, ctx) => {
      if (!val || val.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Email is required",
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid email",
        });
      }
    }),
  password: z
    .string()
    .superRefine((val, ctx) => {
      if (!val || val.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password is required",
        });
      }
    }),

});


type LoginValues = {
  email: string;
  password: string;
  isChecked: boolean;
};

const initialValues: LoginValues = {
  email: "",
  password: "",
  isChecked: false,
};
type FormSchemaType = z.infer<typeof formSchema>;

type LoginFormProps = {
  onSuccess: (email: any) => void;
};

const useStyles = makeStyles((theme) => ({
  customTextField: {
    "& input::placeholder": {
      fontSize: "14px",
    },
  },
}));

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  // const { login, isLoggingIn } = useAuth();
  const classes = useStyles();
  const [errors, setErrors] = useState<z.ZodIssue[]>([]);
  const theme = useTheme();
  const [Icon, setIcon] = React.useState(activeMail);
  const [passwordIcon, setPasswordIcon] = React.useState(inActiveEye);
  const [searchParams] = useSearchParams();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  console.log(searchParams.get("query"));
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
  });


  const [isChecked, setIsChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [apiError, setApiError] = useState("");


  useEffect(() => {
    const remeberMe = storage.getRemeberMe();
    const userName = storage.getUserName();
    if (remeberMe && userName) {
      let emailValue = storage.getUserName();
      let passwordValue = storage.getPassword();
      let dataValue = {
        email: emailValue,
        password: passwordValue,
        isChecked: true,
      };
      storage.setUserNameAndPassword(dataValue);

      setIsChecked(true);
    }
  }, []);



  const handlePassword = () => {
    setShowPassword(!showPassword);
    if (showPassword === true) setPasswordIcon(inActiveBlueEye);
    else setPasswordIcon(blueEye);
  };


  const onSubmit = async (values: LoginValues) => {
    setIsFormSubmitting(true);
    try {
      if (values.isChecked && values.email !== "") {
        storage.setUserNameAndPassword({
          email: values.email,
          password: values.password,
        });
        storage.setCheckBox(values.isChecked);
      }
      await loginWithEmailAndPassword(values);
      onSuccess(values.email);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message;
      setApiError(message);
    } finally {
      setIsFormSubmitting(false);
    }
  };
  const validate = (values: FormSchemaType) => {
    const result = formSchema.safeParse(values);
    if (!result.success) {
      return result.error.flatten().fieldErrors;
    }
    return {};
  };
  return (
    <>

      <Box
        className="mervlogo"
        sx={{
          paddingRight: "10px",
          paddingTop: "14px",
          display: { xs: "none", md: "block", sm: "none" },
        }}
      >
        <Link variant="h3" href="/">
          <img src={logoCrypto} className="logoImage" />
        </Link>
      </Box>
      <Typography component="h1" variant="h1" sx={{ mt: 1 }}>
        Welcome <img src={waving} alt="waving" />
      </Typography>
      <Typography component="h6" variant="h6">
        Please enter your credentials to sign in.
      </Typography>
      <Box sx={{ mt: 3.25, maxWidth: "700px" }}>
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={12} sx={{ pt: { md: 2 } }}>
                  <FormControl
                    sx={{ width: { xs: "100%", md: "calc(100% - 50px)" } }}
                  >
                    <InputLabel
                      htmlFor="email"
                      shrink={true}
                      sx={{ color: "#FFFFFF" }}
                    >
                      Email Address
                    </InputLabel>
                    <TextField
                      sx={{
                        fontSize: {
                          xs: "14px !important",
                          sm: "14px !important",
                          md: "14px !important",
                        },
                        width: "100%",
                      }}
                      margin="normal"

                      classes={{ root: classes.customTextField }}
                      id="email"
                      {...register("email")}
                      autoFocus
                      autoComplete="email"
                      onChange={(e) => {
                        if (apiError) setApiError("");
                        setFieldValue("email", e.target.value);
                      }}
                      value={values.email}
                      placeholder="Enter your Mail Id"
                      onFocus={(e) => {
                        setIcon(activeMail);
                      }}
                      onBlur={(e) => {
                        setIcon(inActiveMail);
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment disableTypography position="end">
                            <img src={Icon} alt="inactiveEye" />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        inputMode: "text", // Ensures the text keyboard is displayed on mobile
                      }}
                    />
                    {touched.email && errors.email && (
                      <div style={{ color: "#FF0000", fontSize: "14px" }}>
                        {errors.email}
                      </div>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={12} sx={{ pt: { md: 2 } }}>
                  <FormControl
                    sx={{ width: { xs: "100%", md: "calc(100% - 50px)" } }}
                  >
                    <InputLabel
                      htmlFor="password"
                      shrink={true}
                      sx={{ color: "#FFFFFF" }}
                    >
                      Password
                    </InputLabel>
                    <TextField
                      margin="normal"
                      type={showPassword ? "text" : "password"}

                      classes={{ root: classes.customTextField }}
                      pattern="[A-Za-z0-9]*"
                      fullWidth
                      id="password"
                      {...register("password")}
                      onChange={(e) => {
                        if (apiError) setApiError("");
                        setFieldValue("password", e.target.value);
                      }}
                      autoComplete="password"
                      value={values.password}
                      placeholder="Enter your Password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            disableTypography
                            position="end"
                            onClick={handlePassword}
                          >
                            <img src={passwordIcon} alt="inactiveEye" />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        inputMode: "text", // Ensures the text keyboard is displayed on mobile
                      }}
                      onFocus={(e) => {
                        setPasswordIcon(inActiveBlueEye);
                      }}
                      onBlur={(e) => {
                        setPasswordIcon(inActiveEye);
                        // setPasswordIcon(inActiveEye);
                      }}
                    />
                    {touched.password && errors.password && (
                      <div style={{ color: "#FF0000", fontSize: "14px" }}>
                        {errors.password}
                      </div>
                    )}
                    {apiError && (
                      <div
                        style={{
                          color: "#FF0000",
                          fontSize: "14px",
                          marginTop: "8px",
                        }}
                      >
                        {apiError.toUpperCase()}
                      </div>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container>

                <Grid
                  item
                  xs
                  sx={{
                    justifyContent: "end",
                    display: "flex",
                    paddingRight: { xs: "0", sm: "calc(100% - 90%)" },
                    marginBlock: { xs: "20px", sm: "10px" },
                  }}
                >
                  <Link
                    href="/auth/forgetPassword"
                    variant="h3"
                    color="#ff9525"
                    underline="none"
                  >
                    Forgot your password?
                  </Link>
                </Grid>
              </Grid>
              {isFormSubmitting ? (
                <CircularProgress size={24} color="inherit" /> // Show the loader
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    mt: { sm: 1, md: 3 },
                    mb: 2,
                    background: "linear-gradient(111deg, #ff9525, #ff5448)",
                    padding: { xs: "10px 15px", sm: "10px 30px", md: "10px 70px" },
                  }}
                >
                  Sign In
                </Button>
              )}
            </Form>
          )}
        </Formik>
        <Grid container>
          <Grid item xs>
            <Typography variant="h3">
              Don't have an account?{" "}
              <Link
                href="/auth/register"
                variant="h3"
                underline="none"
                sx={{ pl: "4px", color: "#ff9525" }}
              >
                Sign Up
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
